import * as React from 'react';

import { ITheme } from '@fluentui/react';

import { getTopLevelKeytipProps } from '@1js/acui-keytip';
import { AppResizeGroup } from '@1js/acui-resize-group';
import { AppThemeConsumer } from '@1js/acui-theme';

import { addLayoutCacheKeyToRibbonTopBarState, RibbonTopBarProps } from '.';
import { TabList } from '../TabList';
import { splitTabHeadersIntoOverflow } from '../TabList/splitTabHeadersIntoOverflow';
import { getRibbonTopBarClassNames } from './RibbonTopBar.styles';

export const TabHeaders: React.StatelessComponent<RibbonTopBarProps & RibbonTopBarScalingState> = (
  props: RibbonTopBarProps & RibbonTopBarScalingState
) => {
  return (
    <AppThemeConsumer>
      {(theme: ITheme) => (
        <div className={getRibbonTopBarClassNames(theme).root}>
          <TabList
            label={props.appStrings.tabsContainerLabel}
            {...splitTabHeadersIntoOverflow(props.ribbonTabs, props.overflowTabCount, props.selectedTabId)}
            onTabHeaderClick={props.onTabHeaderClick}
            onOverflowButtonExecute={props.onTabHeadersOverflowButtonExecute}
            displayDynamicTabHeaderIndicator={props.displayDynamicTabHeaderIndicator}
            selectedTabId={props.selectedTabId}
            appColors={props.appColors}
            overflowTabHeaderMenuLauncherIcon={props.overflowTabHeaderMenuLauncherIcon}
            keytipProps={
              props.appStrings.overflowWellKeytip
                ? getTopLevelKeytipProps(props.appStrings.overflowWellKeytip)
                : undefined
            }
            overflowMenuLauncherLabel={props.appStrings.tabsContainerOverflowLabel}
          />
        </div>
      )}
    </AppThemeConsumer>
  );
};

export interface RibbonTopBarScalingState {
  overflowTabCount: number;
}

export class RibbonTopBar extends React.PureComponent<RibbonTopBarProps, {}> {
  public render() {
    return (
      <AppResizeGroup
        data={this.getInitialTopBarScalingState()}
        onReduceData={this.scaleTopBarDownWithCacheKey}
        onGrowData={this.scaleTopBarUpWithCacheKey}
        onRenderData={this.renderTopBar}
        onAfterScalingComplete={this.props.onAfterScalingCompleted}
      />
    );
  }

  private getInitialTopBarScalingState(): RibbonTopBarScalingState {
    return { overflowTabCount: 0 };
  }

  private scaleTopBarDown = (data: RibbonTopBarScalingState) => {
    if (data.overflowTabCount < this.props.ribbonTabs.length) {
      return { overflowTabCount: data.overflowTabCount + 1 };
    }
    return undefined; // stop scaling down
  };

  private scaleTopBarUp = (data: RibbonTopBarScalingState) => {
    if (data.overflowTabCount > 0) {
      return { overflowTabCount: data.overflowTabCount - 1 };
    }
    return undefined; // stop scaling up
  };

  private scaleTopBarDownWithCacheKey = (scalingState: RibbonTopBarScalingState) => {
    return addLayoutCacheKeyToRibbonTopBarState(this.props, this.scaleTopBarDown(scalingState));
  };

  private scaleTopBarUpWithCacheKey = (scalingState: RibbonTopBarScalingState) => {
    return addLayoutCacheKeyToRibbonTopBarState(this.props, this.scaleTopBarUp(scalingState));
  };

  private renderTopBar = (data: RibbonTopBarScalingState) => {
    return <TabHeaders {...data} {...this.props} />;
  };
}
